import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Brain, Compass, Eye, Handshake, Scale, Gavel, ChevronRight,
         Trophy, Loader2, RotateCcw, ArrowLeft } from 'lucide-react';
import { BACKEND_URL } from '../../config/canonical';

const TOKEN_KEY = 'nexus_token';

const apiCall = async (path, opts = {}) => {
  const token = localStorage.getItem(TOKEN_KEY);
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const KIND_META = {
  skip_trace:          { icon: Compass,   tone: 'border-amber-500/50',   chip: 'bg-amber-500/15 text-amber-200 border-amber-400/30',  label: 'SKIP TRACE' },
  surveillance:        { icon: Eye,       tone: 'border-blue-500/50',    chip: 'bg-blue-500/15 text-blue-200 border-blue-400/30',     label: 'SURVEILLANCE' },
  bail_bonds:          { icon: Handshake, tone: 'border-rose-500/50',    chip: 'bg-rose-500/15 text-rose-200 border-rose-400/30',     label: 'BAIL BONDS' },
  civil_investigation: { icon: Scale,     tone: 'border-emerald-500/50', chip: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/30', label: 'CIVIL' },
  criminal_defense:    { icon: Gavel,     tone: 'border-purple-500/50',  chip: 'bg-purple-500/15 text-purple-200 border-purple-400/30',  label: 'CRIMINAL DEFENSE' },
};

const BandPill = ({ band }) => {
  const map = {
    DISTINCTION: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/40',
    PASS:        'bg-blue-500/20 text-blue-300 border-blue-400/40',
    PASS_LOW:    'bg-amber-500/20 text-amber-300 border-amber-400/40',
    FAIL:        'bg-rose-500/20 text-rose-300 border-rose-400/40',
  };
  const label = band === 'PASS_LOW' ? 'PASS (LOW)' : band;
  return <span className={`text-[10px] font-mono uppercase tracking-[0.18em] border rounded px-2 py-0.5 ${map[band] || ''}`}>{label}</span>;
};

const SimulatorRunner = ({ kind, onExit, onComplete }) => {
  const [sim, setSim] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiCall(`/api/training-center/simulators/${kind}`);
        if (alive) setSim(r);
      } catch (e) { if (alive) toast.error(e.message); }
    })();
    return () => { alive = false; };
  }, [kind]);

  const pickOption = (choiceIndex) => {
    const step = sim.steps[stepIdx];
    const next = [...answers, { step_id: step.id, choice_index: choiceIndex }];
    setAnswers(next);
    if (stepIdx < sim.steps.length - 1) {
      setStepIdx(stepIdx + 1);
    } else {
      submit(next);
    }
  };

  const submit = async (final) => {
    setSubmitting(true);
    try {
      const r = await apiCall(`/api/training-center/simulators/${kind}/submit`, {
        method: 'POST', body: JSON.stringify({ answers: final }),
      });
      setResult(r);
      if (onComplete) onComplete(r);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const restart = () => {
    setStepIdx(0);
    setAnswers([]);
    setResult(null);
  };

  if (!sim) {
    return <div className="text-slate-400 italic flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading simulator…</div>;
  }

  if (submitting) {
    return <div className="text-amber-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Scoring your decisions…</div>;
  }

  if (result) {
    const s = result.scoring;
    return (
      <div className="space-y-4" data-testid="sim-result">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <button onClick={onExit} className="text-xs font-mono uppercase tracking-wider text-slate-300 hover:text-amber-300 inline-flex items-center gap-1 border border-slate-700 rounded px-2.5 py-1" data-testid="sim-back-to-list">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to simulators
          </button>
          <button onClick={restart} className="text-xs font-mono uppercase tracking-wider text-amber-300 hover:text-amber-200 inline-flex items-center gap-1 border border-amber-500/40 rounded px-2.5 py-1" data-testid="sim-restart">
            <RotateCcw className="w-3.5 h-3.5" /> Retry
          </button>
        </div>
        <Card className="bg-[#131A2A] border-[#1E293B]">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 flex-wrap">
              <Trophy className="w-8 h-8 text-amber-300" />
              <div className="min-w-0">
                <p className="text-[10px] font-mono uppercase tracking-[0.20em] text-amber-300">Final Score</p>
                <p className="text-3xl font-extrabold text-white">{s.percent}%</p>
              </div>
              <div className="ml-auto"><BandPill band={s.band} /></div>
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-5 gap-2" data-testid="sim-criteria">
              {Object.entries(s.criteria_scores).map(([k, v]) => (
                <div key={k} className="rounded border border-slate-700 bg-[#0E1626] p-2">
                  <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-slate-400">{k}</p>
                  <p className="text-sm font-bold text-white mt-0.5">{v}/{s.criteria_max[k]}</p>
                  <div className="h-1 w-full bg-slate-800 rounded-full overflow-hidden mt-1">
                    <div className="h-full bg-amber-500" style={{ width: `${Math.round(100 * v / s.criteria_max[k])}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#131A2A] border-[#1E293B]">
          <CardContent className="p-5">
            <h5 className="text-sm font-bold text-white uppercase tracking-wider font-mono mb-3">Step-by-step feedback</h5>
            <ol className="space-y-3">
              {s.step_feedback.map((f, i) => {
                const stepTotal = Object.values(f.scores || {}).reduce((a, b) => a + b, 0);
                const good = stepTotal >= 18;
                return (
                  <li key={i} className={`rounded border ${good ? 'border-emerald-500/40' : stepTotal >= 12 ? 'border-amber-500/40' : 'border-rose-500/40'} bg-[#0E1626] p-3`} data-testid={`sim-feedback-${i}`}>
                    <p className="text-[10px] font-mono uppercase tracking-[0.16em] text-amber-300">Step {i + 1}</p>
                    <p className="text-sm text-white font-semibold mt-0.5">{f.step_prompt}</p>
                    <p className="text-xs text-slate-300 mt-1.5"><span className="font-mono text-slate-500">Your choice → </span>{f.choice_label}</p>
                    <p className="text-xs text-slate-200 mt-1.5"><span className="font-mono text-amber-300">Feedback: </span>{f.feedback}</p>
                  </li>
                );
              })}
            </ol>
          </CardContent>
        </Card>
      </div>
    );
  }

  const step = sim.steps[stepIdx];
  return (
    <div className="space-y-4" data-testid="sim-runner">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={onExit} className="text-xs font-mono uppercase tracking-wider text-slate-300 hover:text-amber-300 inline-flex items-center gap-1 border border-slate-700 rounded px-2.5 py-1" data-testid="sim-exit">
          <ArrowLeft className="w-3.5 h-3.5" /> Exit
        </button>
        <p className="text-[10px] font-mono uppercase tracking-[0.20em] text-slate-400">
          STEP {stepIdx + 1} of {sim.total_steps}
        </p>
      </div>
      <Card className="bg-[#131A2A] border-[#1E293B]">
        <CardContent className="p-5">
          <p className="text-[10px] font-mono uppercase tracking-[0.20em] text-amber-300">{sim.title}</p>
          <h4 className="text-base font-bold text-white mt-1">{step.prompt}</h4>
          <p className="text-xs text-slate-400 mt-2 leading-relaxed">{step.situation}</p>
          {stepIdx === 0 && (
            <p className="text-xs text-slate-300 italic mt-3 border-t border-slate-700 pt-3"><span className="font-mono text-amber-300">Briefing: </span>{sim.scenario}</p>
          )}
          <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden mt-4">
            <div className="h-full bg-amber-500 transition-all" style={{ width: `${Math.round(100 * (stepIdx) / sim.total_steps)}%` }} />
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sim-options">
        {step.options.map((opt, i) => (
          <button
            key={i}
            onClick={() => pickOption(i)}
            className="text-left rounded-lg border border-slate-700 hover:border-amber-500/60 bg-[#0E1626] hover:bg-[#13203A] p-4 transition-colors group"
            data-testid={`sim-option-${i}`}
          >
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-slate-800 border border-slate-600 group-hover:border-amber-500 text-amber-300 font-bold text-xs inline-flex items-center justify-center flex-shrink-0">{String.fromCharCode(65 + i)}</span>
              <p className="text-sm text-slate-100 leading-snug">{opt.label}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const SimulatorsPanel = () => {
  const [sims, setSims] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadList = async () => {
    try {
      const [list, mine] = await Promise.all([
        apiCall('/api/training-center/simulators'),
        apiCall('/api/training-center/simulators/attempts/me'),
      ]);
      setSims(list.simulators || []);
      setAttempts(mine.attempts || []);
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [list, mine] = await Promise.all([
          apiCall('/api/training-center/simulators'),
          apiCall('/api/training-center/simulators/attempts/me'),
        ]);
        if (!alive) return;
        setSims(list.simulators || []);
        setAttempts(mine.attempts || []);
      } catch (e) { if (alive) toast.error(e.message); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const bestByKind = useMemo(() => {
    const best = {};
    for (const a of attempts) {
      const k = a.simulator_kind;
      const pct = a.scoring?.percent || 0;
      if (!best[k] || pct > best[k].percent) {
        best[k] = { percent: pct, band: a.scoring?.band };
      }
    }
    return best;
  }, [attempts]);

  if (active) {
    return <SimulatorRunner kind={active} onExit={() => setActive(null)} onComplete={() => loadList()} />;
  }

  return (
    <div className="space-y-4" data-testid="simulators-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sim-summary">
        <div className="rounded-lg border-2 border-amber-400/30 bg-amber-500/5 p-3">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300">Simulators</p>
          <p className="text-xl font-bold text-white mt-0.5">{sims.length}</p>
        </div>
        <div className="rounded-lg border-2 border-emerald-400/30 bg-emerald-500/5 p-3">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-300">Completions</p>
          <p className="text-xl font-bold text-white mt-0.5">{Object.keys(bestByKind).length}</p>
        </div>
        <div className="rounded-lg border-2 border-blue-400/30 bg-blue-500/5 p-3">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-blue-300">Attempts</p>
          <p className="text-xl font-bold text-white mt-0.5">{attempts.length}</p>
        </div>
        <div className="rounded-lg border-2 border-slate-500/30 bg-slate-500/5 p-3">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-300">Avg %</p>
          <p className="text-xl font-bold text-white mt-0.5">
            {attempts.length === 0 ? '—' : Math.round(attempts.reduce((s, a) => s + (a.scoring?.percent || 0), 0) / attempts.length) + '%'}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400 italic">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sim-grid">
          {sims.map(s => {
            const meta = KIND_META[s.kind] || KIND_META.skip_trace;
            const Icon = meta.icon;
            const best = bestByKind[s.kind];
            return (
              <Card key={s.id} className={`bg-[#131A2A] ${meta.tone}`} data-testid={`sim-card-${s.kind}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="w-10 h-10 rounded-md bg-[#0E1626] border border-slate-700 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-amber-300" />
                    </div>
                    <span className={`text-[10px] font-mono uppercase tracking-[0.18em] border rounded px-2 py-0.5 ${meta.chip}`}>{meta.label}</span>
                  </div>
                  <h4 className="text-base font-bold text-white mt-3">{s.title}</h4>
                  <p className="text-xs text-slate-400 mt-1.5 line-clamp-3">{s.summary}</p>
                  <p className="text-[11px] font-mono text-slate-500 mt-3">{s.total_steps} decision steps · 5-criteria scoring</p>
                  {best && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-slate-300 font-mono">Best: {best.percent}%</span>
                      <BandPill band={best.band} />
                    </div>
                  )}
                  <div className="mt-4 pt-3 border-t border-slate-700/60">
                    <button
                      onClick={() => setActive(s.kind)}
                      className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold text-sm rounded-md px-3 py-2 inline-flex items-center justify-center gap-1.5"
                      data-testid={`sim-launch-${s.kind}`}
                    >
                      <Brain className="w-4 h-4" /> {best ? 'Replay simulator' : 'Launch simulator'}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SimulatorsPanel;
